import * as React from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button, Form, Message, Modal} from 'semantic-ui-react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import * as Generated from '../generated/graphql';
import {propagateErrorsContext} from '../util/errors';
import {
  PORTFOLIO_COLLECTION_DISPLAY_NAME,
  SEQUENCE_COLLECTION_DISPLAY_NAME,
} from './artifactConstants';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {automaticallyGeneratedTypeNames} from './ArtifactSidebar/ArtifactSidebar';

const PortfolioTypeNameAllowList = new Set(['model']);

const CreateArtifactTableRow = styled.tr`
  height: 50px;
  td:nth-child(1) {
    text-align: right;
    font-weight: bold;
  }
  td:nth-child(2) {
    width: 12px;
  }
`;

type CreateArtifactPayload = {
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  artifactName: string;
};

export const CreateArtifactModal: React.FC<{
  entityName: string;
  projectName: string;
  open: boolean;
  onCloseAction: () => void;
  afterCreate: (payload: CreateArtifactPayload) => void;
}> = props => {
  const [createArtifactTypesMutation] =
    Generated.useCreateArtifactTypesMutation({
      context: propagateErrorsContext(),
    });
  const [createPortfolioMutation] =
    Generated.useCreateArtifactPortfolioMutation({
      context: propagateErrorsContext(),
    });
  const [createSequenceMutation] = Generated.useCreateArtifactSequenceMutation({
    context: propagateErrorsContext(),
  });
  const artifactTypesQuery = Generated.useArtifactTypesQuery({
    variables: {
      projectName: props.projectName,
      entityName: props.entityName,
    },
  });
  const fetchedTypes: string[] = React.useMemo(() => {
    return (
      (artifactTypesQuery.data?.project?.artifactTypes.edges
        .map(at => at.node?.name)
        .filter(n => n != null) as string[]) ?? []
    );
  }, [artifactTypesQuery.data]);
  const artifactTypeOptions = React.useMemo(() => {
    return Array.from(new Set(['model', ...fetchedTypes]))
      .filter(name => !automaticallyGeneratedTypeNames.has(name))
      .map(t => ({text: t.toLowerCase(), value: t.toLowerCase()}));
  }, [fetchedTypes]);
  const [selectedType, setSelectedType] = React.useState(
    artifactTypeOptions[0].value
  );
  const [selectedStyle, setSelectedStyle] = React.useState('sequence');
  const [providedName, setProvidedName] = React.useState<string | null>(null);

  const resetState = React.useCallback(() => {
    setSelectedType(artifactTypeOptions[0].value);
    setSelectedStyle('sequence');
    setProvidedName(null);
  }, [artifactTypeOptions]);

  // this is an client-side constraint - there is nothing in the
  // backend that disallows (or should disallow) creating Portfolios
  // for types other than 'model'. However, we are keeping it simple
  // for now.
  const showStyle = PortfolioTypeNameAllowList.has(selectedType);
  const resolvedStyle = showStyle ? selectedStyle : 'sequence';

  const onClose = React.useCallback(() => {
    resetState();
    props.onCloseAction();
  }, [props, resetState]);

  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = React.useCallback(() => {
    if (!submitting && providedName != null) {
      setSubmitting(true);
      const createArtifactMutation =
        resolvedStyle === 'portfolio'
          ? createPortfolioMutation
          : createSequenceMutation;
      createArtifactTypesMutation({
        variables: {
          entityName: props.entityName,
          projectName: props.projectName,
          artifactTypes: [{name: selectedType}],
        },
      })
        .then(res => {
          const artifactTypes = res.data?.createArtifactTypes?.artifactTypes;
          if (artifactTypes == null || artifactTypes.length === 0) {
            throw new Error('Failed to upsert artifact type');
          }
          const artifactTypeId = artifactTypes[0].id;
          return createArtifactMutation({
            variables: {
              entityName: props.entityName,
              projectName: props.projectName,
              artifactTypeID: artifactTypeId,
              name: providedName,
              description: '',
            },
          });
        })
        .then(() => {
          resetState();
          setSubmitting(false);
          props.afterCreate({
            entityName: props.entityName,
            projectName: props.projectName,
            artifactTypeName: selectedType,
            artifactName: providedName,
          });
        })
        .catch(reason => {
          toast(`Unable to create ${providedName}`);
          console.error(reason);
          onClose();
        });
    }
  }, [
    createArtifactTypesMutation,
    createPortfolioMutation,
    createSequenceMutation,
    onClose,
    props,
    providedName,
    resetState,
    resolvedStyle,
    selectedType,
    submitting,
  ]);

  return (
    <Modal open={props.open} onClose={onClose}>
      <Modal.Header>
        <p>Create New</p>
      </Modal.Header>
      <Modal.Content>
        <Form>
          <table style={{width: '100%'}}>
            <tbody>
              <CreateArtifactTableRow>
                <td>
                  <Form.Field>Entity</Form.Field>
                </td>
                <td></td>
                <td>{props.entityName}</td>
              </CreateArtifactTableRow>
              <CreateArtifactTableRow>
                <td>
                  <Form.Field>Project</Form.Field>
                </td>
                <td></td>
                <td>{props.projectName}</td>
              </CreateArtifactTableRow>
              <CreateArtifactTableRow>
                <td>
                  <Form.Field>Type</Form.Field>
                </td>
                <td></td>
                <td>
                  <Form.Dropdown
                    value={selectedType}
                    options={artifactTypeOptions}
                    button
                    selection
                    search
                    onChange={(e, d) => {
                      setSelectedType('' + d.value);
                    }}
                  />
                </td>
              </CreateArtifactTableRow>
              {showStyle && (
                <>
                  <CreateArtifactTableRow>
                    <td>
                      <Form.Field>Style</Form.Field>
                    </td>
                    <td></td>
                    <td>
                      <Form.Dropdown
                        value={selectedStyle}
                        options={[
                          {
                            text: SEQUENCE_COLLECTION_DISPLAY_NAME,
                            value: 'sequence',
                          },
                          {
                            // TODO: Make this artifact type-agnostic when we start doing Registered Datasets.
                            text: `${PORTFOLIO_COLLECTION_DISPLAY_NAME} Model`,
                            value: 'portfolio',
                          },
                        ]}
                        button
                        selection
                        search
                        onChange={(e, d) => {
                          setSelectedStyle('' + d.value);
                        }}
                      />
                    </td>
                  </CreateArtifactTableRow>
                  <tr>
                    <td colSpan={2}></td>
                    <td>
                      <Message>
                        An Artifact's style determines how versions can be
                        added:
                        <ul>
                          <li>
                            <strong>{SEQUENCE_COLLECTION_DISPLAY_NAME}s</strong>{' '}
                            contain Artifact Versions which are directly logged
                            via `wandb.log_artifact` and serve as the "home" for
                            the Artifact Version. Every Artifact Version will
                            belong to a single{' '}
                            {SEQUENCE_COLLECTION_DISPLAY_NAME}.
                          </li>
                          <li>
                            <strong>
                              {PORTFOLIO_COLLECTION_DISPLAY_NAME} Models
                            </strong>{' '}
                            act like a folder of "bookmarks" - each version in a{' '}
                            {PORTFOLIO_COLLECTION_DISPLAY_NAME} Model is
                            actually a link to an Artifact Version belonging to
                            an {SEQUENCE_COLLECTION_DISPLAY_NAME}&nbsp;of the
                            same type. An Artifact Version may be linked to any
                            number of {PORTFOLIO_COLLECTION_DISPLAY_NAME}{' '}
                            Models.
                          </li>
                        </ul>
                      </Message>
                    </td>
                  </tr>
                </>
              )}
              <CreateArtifactTableRow>
                <td>
                  <Form.Field>Name</Form.Field>
                </td>
                <td></td>
                <td>
                  <Form.Input
                    onChange={(e, d) => {
                      setProvidedName(d.value);
                    }}
                  />
                </td>
              </CreateArtifactTableRow>
            </tbody>
          </table>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button basic size="tiny" onClick={props.onCloseAction}>
          Cancel
        </Button>
        <Button
          positive
          size="tiny"
          onClick={onSubmit}
          disabled={
            submitting || providedName == null || providedName.length < 4
          }>
          {submitting ? 'Creating...' : 'Create'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
